import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { FaCookie, FaTimes, FaCog } from 'react-icons/fa';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    performance: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      applyConsent(savedPreferences);
    }
  }, []);

  const applyConsent = (prefs) => {
    // Apply Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied'
      });
    }

    // Apply Microsoft Clarity consent
    if (prefs.performance) {
      // Enable Clarity if not already enabled
      if (!window.clarity) {
        const script = document.createElement('script');
        script.innerHTML = `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "srvt2xopve");
        `;
        document.head.appendChild(script);
      }
    } else {
      // Disable Clarity if enabled
      if (window.clarity) {
        window.clarity('stop');
      }
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      performance: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent(allAccepted);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      performance: false
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent(onlyNecessary);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    applyConsent(preferences);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <CookieWrapper>
      <CookieContainer>
        {!showPreferences ? (
          <>
            <CookieHeader>
              <CookieIcon>
                <FaCookie />
              </CookieIcon>
              <CookieTitle>We use cookies</CookieTitle>
              <CloseButton onClick={() => setIsVisible(false)}>
                <FaTimes />
              </CloseButton>
            </CookieHeader>
            
            <CookieContent>
              <CookieText>
                We use cookies to enhance your browsing experience. 
                By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or learn more in our{' '}
                <PolicyLink to="/privacy-policy">Privacy Policy</PolicyLink>.
              </CookieText>
              
              <CookieActions>
                <ActionButton onClick={handleRejectAll} variant="secondary">
                  Reject All
                </ActionButton>
                <ActionButton onClick={() => setShowPreferences(true)} variant="outline">
                  <FaCog style={{ marginRight: '5px' }} />
                  Manage Preferences
                </ActionButton>
                <ActionButton onClick={handleAcceptAll} variant="primary">
                  Accept All
                </ActionButton>
              </CookieActions>
            </CookieContent>
          </>
        ) : (
          <>
            <CookieHeader>
              <CookieTitle>Cookie Preferences</CookieTitle>
              <CloseButton onClick={() => setShowPreferences(false)}>
                <FaTimes />
              </CloseButton>
            </CookieHeader>
            
            <PreferencesContent>
              <PreferenceItem>
                <PreferenceHeader>
                  <PreferenceToggle 
                    checked={preferences.necessary} 
                    disabled
                    readOnly
                  />
                  <PreferenceTitle>Necessary Cookies</PreferenceTitle>
                  <RequiredBadge>Required</RequiredBadge>
                </PreferenceHeader>
                <PreferenceDescription>
                  Essential for website functionality. Cannot be disabled.
                </PreferenceDescription>
              </PreferenceItem>

              <PreferenceItem>
                <PreferenceHeader>
                  <PreferenceToggle 
                    checked={preferences.analytics} 
                    onChange={() => handlePreferenceChange('analytics')}
                  />
                  <PreferenceTitle>Analytics Cookies</PreferenceTitle>
                </PreferenceHeader>
                <PreferenceDescription>
                  Help us understand how visitors interact with our website.
                </PreferenceDescription>
              </PreferenceItem>

              <PreferenceItem>
                <PreferenceHeader>
                  <PreferenceToggle 
                    checked={preferences.performance} 
                    onChange={() => handlePreferenceChange('performance')}
                  />
                  <PreferenceTitle>Performance Cookies</PreferenceTitle>
                </PreferenceHeader>
                <PreferenceDescription>
                  Monitor website performance and user experience.
                </PreferenceDescription>
              </PreferenceItem>

              <PreferenceItem>
                <PreferenceHeader>
                  <PreferenceToggle 
                    checked={preferences.marketing} 
                    onChange={() => handlePreferenceChange('marketing')}
                  />
                  <PreferenceTitle>Marketing Cookies</PreferenceTitle>
                </PreferenceHeader>
                <PreferenceDescription>
                  Used for advertising and marketing purposes.
                </PreferenceDescription>
              </PreferenceItem>

              <PreferenceActions>
                <ActionButton onClick={() => setShowPreferences(false)} variant="secondary">
                  Back
                </ActionButton>
                <ActionButton onClick={handleSavePreferences} variant="primary">
                  Save Preferences
                </ActionButton>
              </PreferenceActions>
            </PreferencesContent>
          </>
        )}
      </CookieContainer>
    </CookieWrapper>
  );
};

// Styled Components
const CookieWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const CookieContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const CookieHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #eee;
  position: relative;
`;

const CookieIcon = styled.div`
  font-size: 24px;
  color: #079487;
  margin-right: 10px;
`;

const CookieTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const CookieContent = styled.div`
  padding: 20px;
`;

const CookieText = styled.p`
  margin: 0 0 20px 0;
  color: #666;
  line-height: 1.6;
  font-size: 14px;
`;

const PolicyLink = styled(RouterLink)`
  color: #079487;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CookieActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #079487;
          color: white;
          border-color: #079487;
          
          &:hover {
            background: #066f64;
            border-color: #066f64;
          }
        `;
      case 'secondary':
        return `
          background: #6c757d;
          color: white;
          border-color: #6c757d;
          
          &:hover {
            background: #5a6268;
            border-color: #5a6268;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #079487;
          border-color: #079487;
          
          &:hover {
            background: #079487;
            color: white;
          }
        `;
      default:
        return '';
    }
  }}
`;

const PreferencesContent = styled.div`
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
`;

const PreferenceItem = styled.div`
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const PreferenceHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const PreferenceToggle = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 12px;
  width: 16px;
  height: 16px;
  accent-color: #079487;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

const PreferenceTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  flex: 1;
`;

const RequiredBadge = styled.span`
  background: #28a745;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const PreferenceDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

const PreferenceActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
`;

export default CookieConsent;
